import { defineStore, storeToRefs } from "pinia";
import { computed, type ComputedRef, type Ref, ref } from "vue";
import {
  type EventProperty,
  type EventPropertyFilter,
  type EventPropertyWithReadableValue,
  type EventType,
  type EventTypeProperty,
  type FabriqEvent,
  type FabriqEventPayload,
  type FabriqEventWithReadableProperties,
  Field,
  Group,
  type LocalizedEventTypeProperty,
  Team,
} from "@/types";
import { type Localizable, localize } from "@/utils/localize";

import { useEventTypesStore } from "./eventTypes";
import { filterEvent } from "@/utils/filter_event";
import { useFabriqStore } from "./fabriq";
import { useEventsWithPropertiesStore } from "./eventsWithProperties";
import { useTeamsStore } from "./teams";
import { useClientStore } from "./client";
import { useFields } from "@/composables/useFields";
import { useEventPermissions } from "@/composables/permissions/useEventPermissions";

export const useEventFiltersStore = defineStore("eventFilters", () => {
  const eventsWithPropertiesStore = useEventsWithPropertiesStore();
  const eventTypeStore = useEventTypesStore();
  const teamsStore = useTeamsStore();
  const clientStore = useClientStore();

  const { locale, user } = useFabriqStore();

  const { can } = useEventPermissions();

  const {
    eventsWithProperties,
    allEventTypesOrdinalPropertiesValues,
    allEventTypesCardinalPropertiesValues,
    allEventTypesDateTimeProperties,
    eventTypePropertiesMap,
  } = storeToRefs(eventsWithPropertiesStore);
  const { collection: eventTypes } = storeToRefs(eventTypeStore);

  const filters = ref<EventPropertyFilter[]>([]);
  const eventTypeId = ref<string | null>(null);

  const teams: ComputedRef<Team[]> = computed(() => {
    if (!teamsStore.collection?.length) return [];
    return teamsStore.collection.map((t: Team) => {
      return {
        ...t,
        color: t.config?.color,
        icon: t.config?.icon,
      };
    });
  });

  const availableZones: Ref<Field[]> = computed(
    getAvailableZonesForTeams(teams, clientStore, locale, user)
  );

  const { organizationFields } = useFields();

  const eventTypeProperties: Ref<EventTypeProperty[]> = ref([]);

  const filterProperties: Ref<LocalizedEventTypeProperty[]> = ref([]);
  const isCompleteFilter: Ref<boolean | undefined> = ref();

  const page = ref(0);
  const asc = ref(false);
  const sortProperty = ref<string | undefined>();
  const teamId = ref<number>();

  const eventList: ComputedRef<FabriqEventWithReadableProperties[]> = computed(
    () => {
      const events = eventsWithProperties.value;
      const eventTypePropertiesMapValue = eventTypePropertiesMap.value;
      const eventTypeIdValue = eventTypeId.value;
      const list = events
        .filter((e: FabriqEvent) =>
          filterEvent(
            e,
            eventTypeIdValue,
            filters.value,
            isCompleteFilter.value
          )
        )
        .map((e: FabriqEvent) => {
          const properties = e.properties
            .sort((a, b) => {
              const orderA =
                eventTypePropertiesMapValue[a.eventPropertyTypeId]?.order;
              const orderB =
                eventTypePropertiesMapValue[b.eventPropertyTypeId]?.order;
              return orderA - orderB;
            })
            .map((property) => {
              return eventPropertyToReadableEventProperty(
                e,
                property,
                eventTypePropertiesMap,
                allEventTypesOrdinalPropertiesValues,
                allEventTypesCardinalPropertiesValues,
                availableZones,
                organizationFields,
                locale
              );
            });

          return { ...e, properties };
        });
      return list;
    }
  );

  function loadEventListWithFilters(options?: { flush: boolean }) {
    const eventTypeFilters = eventTypeId.value
      ? filters.value.length
        ? [
            ...filters.value.map((f, index) => {
              return {
                ...f,
                ...(index ? { conjunction: "AND" } : {}),
              };
            }),
          ]
        : getFilterForAllEventTypes(eventTypeId.value)
      : getFilterForAllEventTypes();
    return eventsWithPropertiesStore.retrieveEvents(
      eventTypeId.value,
      eventTypeFilters,
      isCompleteFilter.value,
      page.value,
      asc.value,
      sortProperty.value,
      teamId.value,
      allEventTypesDateTimeProperties.value,
      options?.flush ?? false
    );
  }

  async function setEventTypeId(id: string | null, load = true) {
    resetFilters();
    eventTypeId.value = id;
    eventTypeProperties.value = [];
    filterProperties.value = [];
    sortProperty.value = undefined;
    if (id) {
      eventTypeProperties.value =
        eventTypes.value.find((eventType: EventType) => eventType.id === id)
          ?.properties ?? [];
      filterProperties.value = [
        ...eventTypeProperties.value.map((p) => {
          if (p.type === "ordinal" || p.type === "cardinal") {
            return {
              ...p,
              label: localize(p.label, locale, 0) ?? "",
              values: p.values.map((v) => ({
                ...v,
                label: localize(v.label, locale, 0) ?? "",
              })),
            } as LocalizedEventTypeProperty;
          }
          return {
            ...p,
            label: localize(p.label, locale, 0) ?? "",
          };
        }),
      ];
      sortProperty.value = eventTypeProperties.value.find(
        (p) => p.type === "datetime"
      )?.id;
    }
    if (load) await loadEventListWithFilters({ flush: true });
  }

  function resetFilters() {
    filters.value = [];
    page.value = 0;
  }
  function setZoneId(zoneId: number | null) {
    if (!eventTypeId.value) return;
    const zoneProperty = eventTypeProperties.value.find(
      (ep) => ep.type === "zone"
    );
    if (!zoneProperty) return;
    filters.value =
      zoneId === null || zoneId === undefined
        ? []
        : [
            {
              conjunction: "AND",
              id: zoneProperty.id,
              operator: "==",
              values: [zoneId],
            },
          ];
    page.value = 0;
    return loadEventListWithFilters({ flush: true });
  }
  function loadMore() {
    page.value++;
    return loadEventListWithFilters();
  }

  function isAvailableForAllSite(eventType: EventType): boolean {
    return eventType.groupIds.length === 0;
  }

  function filteredZoneFromEventType(eventType: EventType) {
    const eventGroupIds = new Set(eventType.groupIds);
    const zones = new Map<number, Field>();
    if (isAvailableForAllSite(eventType)) {
      availableZones.value.forEach((availableZone) => {
        zones.set(availableZone.id, availableZone);
      });
    } else {
      availableZones.value.forEach((availableZone) => {
        if (availableZone.groups.some((group) => eventGroupIds.has(group))) {
          zones.set(availableZone.id, availableZone);
        }
      });
    }

    return [...zones.values()];
  }

  function getFilterForAllEventTypes(eventTypeId?: string) {
    const allFilters: EventPropertyFilter[] = [];
    eventTypes.value.forEach((eventType: EventType) => {
      if (eventTypeId && eventType.id !== eventTypeId) return;
      const zoneProperty = eventType.properties.find(
        (p: EventTypeProperty) => p.type === "zone"
      );
      if (!zoneProperty) return;
      const values = filteredZoneFromEventType(eventType).map((z) => z.id);
      if (!values.length) return;
      allFilters.push({
        id: zoneProperty.id,
        operator: "==",
        values,
        ...(allFilters.length ? { conjunction: "OR" } : {}),
      });
    });
    return allFilters;
  }

  return {
    eventList,
    filterProperties,
    eventTypeId,
    setEventTypeId,
    eventTypeProperties,
    filters,
    sortProperty,
    loadMore,
    sortOrderAscending: asc,
    setZoneId,
    availableZones,
    filteredZoneFromEventType,
    canUserEdit: can.event.editEvent,
    loadEventListWithFilters,
  };
});

export function getZoneIdWithChildren(
  masterZoneId: number,
  zones: Field[]
): number[] {
  const children = zones
    .filter((z) => z.parent === masterZoneId)
    .flatMap((z) => getZoneIdWithChildren(z.id, zones));
  return [masterZoneId, ...children];
}

export function eventPropertyToReadableEventProperty(
  event: FabriqEventPayload,
  property: EventProperty,
  eventTypePropertiesMap: Ref<Record<string, EventTypeProperty>>,
  allEventTypesOrdinalPropertiesValues: Ref<
    Record<
      string,
      Record<
        string,
        {
          id: string;
          rank: number;
          label: Localizable;
          color?: string | undefined;
        }[]
      >
    >
  >,
  allEventTypesCardinalPropertiesValues: Ref<
    Record<
      string,
      Record<
        string,
        {
          id: string;
          value: string;
          label: Localizable;
          color?: string | undefined;
        }[]
      >
    >
  >,
  availableZones: Ref<Field[]>,
  availableFields: Ref<Map<Field["id"], Field>>,
  locale: string
): EventPropertyWithReadableValue {
  const eventProperty =
    eventTypePropertiesMap.value[property.eventPropertyTypeId];

  if (!eventProperty) {
    return { ...property, readableValue: "" };
  }
  return {
    ...property,
    readableValue: getReadableValue(
      property.value,
      event.eventTypeId,
      eventProperty,
      allEventTypesOrdinalPropertiesValues,
      allEventTypesCardinalPropertiesValues,
      availableZones,
      availableFields,
      locale
    ),
  };
}

export function getReadableValue(
  value: string | number | null | undefined,
  eventTypeId: string,
  eventTypeProperty: EventTypeProperty,
  allEventTypesOrdinalPropertiesValues: Ref<
    Record<
      string,
      Record<
        string,
        {
          id: string;
          rank: number;
          label: Localizable;
          color?: string | undefined;
        }[]
      >
    >
  >,
  allEventTypesCardinalPropertiesValues: Ref<
    Record<
      string,
      Record<
        string,
        {
          id: string;
          value: string;
          label: Localizable;
          color?: string | undefined;
        }[]
      >
    >
  >,
  availableZones: Ref<Field[]>,
  availableFields: Ref<Map<Field["id"], Field>>,
  locale: string
): string {
  switch (eventTypeProperty.type) {
    case "datetime":
      return value !== undefined && value !== null ? `${value}` : "";
    case "ordinal": {
      const ordinalPropertyValues =
        allEventTypesOrdinalPropertiesValues.value[eventTypeId];
      if (!ordinalPropertyValues) throw new Error("Ordinal values not found");
      const readableValue = (
        ordinalPropertyValues[eventTypeProperty.id] ?? []
      ).find((v) => v.id === value);
      return readableValue?.label
        ? localize(readableValue.label, locale, 0) ?? ""
        : "";
    }
    case "cardinal": {
      const cardinalPropertyValues =
        allEventTypesCardinalPropertiesValues.value[eventTypeId];
      if (!cardinalPropertyValues) throw new Error("Cardinal values not found");
      const readableValue = (
        cardinalPropertyValues[eventTypeProperty.id] ?? []
      ).find((v) => v.id === value);
      return readableValue?.label
        ? localize(readableValue.label, locale, 0) ?? ""
        : "";
    }
    case "zone":
      const zone = availableZones.value.find((z) => z.id === value);
      return zone ? zone.name : "";
    case "equipment":
    case "product":
      const field = availableFields.value.get(value as number);
      return field?.name ? localize(field.name, locale, 0) ?? "" : "";
    default:
      return value !== undefined && value !== null ? `${value}` : "";
  }
}

export function getAvailableZonesForTeams(
  teams: Ref<Team[]>,
  clientStore: any,
  locale: any,
  user: {
    id: number;
  } | null
) {
  return () => {
    const zonesOfEventsById: Map<string, Field> = new Map();

    // Extra fields are either linked to a group, or to the client itself.
    // In order to get the extra fields of a ticket, we must get the extra fields
    // of any of its teams, which are the sum of the extra fields of their groups
    // and the extra fields of the client.
    const idsOfGroupsOfAnyTeam = new Set([
      ...teams.value.flatMap((team: Team) => {
        return team.groups;
      }),
      ...clientStore.groups
        .filter(
          (group: Group) =>
            user?.id !== undefined && group.admins.includes(user.id)
        )
        .map((group: Group) => group.id),
    ]);

    const allZones: Field[] = [
      ...clientStore.groups
        .filter((group: Group) => idsOfGroupsOfAnyTeam.has(group.id))
        .flatMap((group: Group) =>
          group.extra_fields.filter((v: Field) => v.type === "zone")
        ),
      ...clientStore.extra_fields.filter((v: Field) => v.type === "zone"),
    ];

    const allUsableZones: Field[] = teams.value
      .flatMap((team: Team) => team.extra_fields)
      .filter((id) => allZones.some((zone) => zone.id === id))
      .flatMap((id) => [id, ...getZoneIdWithChildren(id, allZones)])
      .map((id) => allZones.find((zone) => zone.id === id) as Field);

    allUsableZones.forEach((zone) => {
      zonesOfEventsById.set(`${zone.id}`, zone);
    });

    return Array.from(zonesOfEventsById.values()).map((v: any) => ({
      ...v,
      name: v?.config?.i18n?.[locale] || v.name,
    }));
  };
}
